import { GetStaticProps } from 'next';
import type { CSSProperties } from 'react';
import Head from 'next/head';
import { clsx } from '@wonderful/wwc/dist/src/helpers/clsx';
import { useTranslation, withTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { TranslationFunction, KeysParam } from '../translation-types';
import type TranslationCommon from '../public/static/locales/en-us/common.json';
// Importing translations for use in getStaticProps
import * as enTranslations from '../public/static/locales/en-us/common.json';
import * as deTranslations from '../public/static/locales/de/common.json';
import * as esTranslations from '../public/static/locales/es/common.json';
import * as frTranslations from '../public/static/locales/fr/common.json';
import * as itTranslations from '../public/static/locales/it/common.json';
import * as nlTranslations from '../public/static/locales/nl/common.json';
import * as arTranslations from '../public/static/locales/ar/common.json';

import { LocaleEnum } from '@store/enums';

import Section from '@design/Section/Section';
import HeadingBlock from '@builder/HeadingBlock/HeadingBlock';
import styles from '@styles/pages/sitemap.module.scss';

const translationDictionary = {
  'en-us': enTranslations,
  de: deTranslations,
  es: esTranslations,
  fr: frTranslations,
  it: itTranslations,
  nl: nlTranslations,
  ar: arTranslations,
};

interface Props {
  pages: { locale: string }[];
  t: TranslationFunction<'common'>;
}

function Sitemap(props: Props) {
  const { pages } = props;
  const { t } = useTranslation('common');

  const headingBlockContent = {
    header: t('Sitemap'),
    image: {
      src: '/images/contact/pistachios_shell-noshell.png',
      altText: '',
    },
  };

  return (
    <>
      <Head>
        <title>{`${t('Sitemap')} | EMEA Pistachios`}</title>
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_WEB_URL}sitemap`}
        />
      </Head>
      <HeadingBlock {...headingBlockContent} />
      <Section className={clsx(styles.sitemap, 'container')}>
        {pages.map((p: any, index: number) => (
          <div key={index}>
            <div>{p.locale}</div>
            <ul>
              {p.pages.map((page: any, index: number) => (
                <Page key={index} page={page} level={1} t={t} />
              ))}
            </ul>
          </div>
        ))}
      </Section>
    </>
  );
}
interface PageProps {
  page: {
    slug: string;
    title: KeysParam<'common'>;
    children: PageProps[];
  };
  level: number;
  t: TranslationFunction<'common'>;
}

const Page = ({ page, level, t }: PageProps) => {
  return (
    <li
      style={
        {
          '--padding-value': level + 'rem',
        } as CSSProperties
      }
    >
      <a key={page.slug} href={page.slug}>
        {page.title}
      </a>
      {page.children && page.children.length > 0 && (
        <ul>
          {page.children.map((c: any, index: number) => (
            <Page key={index} page={c} level={level + 1} t={t} />
          ))}
        </ul>
      )}
    </li>
  );
};

export default withTranslation('common')(Sitemap);

export const getStaticProps: GetStaticProps = async ({ locale, locales }) => {
  const BUILDER_PUBLIC_API_KEY = process.env.NEXT_PUBLIC_BUILDER_API_KEY;

  const pages = [];
  for (const locale of locales as string[]) {
    const currentPageModel = locale === 'en-us' ? 'page' : `page-${locale}`;

    const currentProductModel =
      locale === 'en-us' ? 'product-page' : `product-page-${locale}`;

    const builderPages = await fetch(
      `https://cdn.builder.io/api/v3/content/${currentPageModel}?apiKey=${BUILDER_PUBLIC_API_KEY}`
    ).then(response => response.json());

    const builderProductPages = await fetch(
      `https://cdn.builder.io/api/v3/content/${currentProductModel}?apiKey=${BUILDER_PUBLIC_API_KEY}`
    ).then(response => response.json());

    // Set what translations to use for page title based on locale
    const translationsCommon = translationDictionary[
      locale as LocaleEnum
    ] as typeof TranslationCommon;

    const sm = {
      locale,
      pages: [
        {
          title: translationsCommon['contact us'] || 'Contact Us',
          slug: `/${locale}/contact`,
        },
        {
          title: translationsCommon['privacy policy'] || 'Privacy Policy',
          slug: `/${locale}/privacy-policy`,
        },
        {
          title: translationsCommon['terms of use'] || 'Terms of Use',
          slug: `/${locale}/terms-of-use`,
        },
        {
          title: translationsCommon['products'] || 'Products',
          slug: `/${locale}/products`,
          children: builderProductPages.results
            ? builderProductPages.results.map(
                (result: {
                  data: { title: string };
                  name: string;
                  query: [{ value: string }];
                }) => ({
                  // SEO title check, instead of page name to help with translations
                  title: result.data.title ? result.data.title : result.name,
                  slug: `${result.query[0].value}`,
                })
              )
            : null,
        },
      ],
    };

    if (builderPages.results) {
      builderPages.results.map(
        (result: {
          data: { title: string };
          name: string;
          query: [{ value: string }];
        }) => {
          if (result) {
            sm.pages.unshift({
              // SEO title check, instead of page name to help with translations
              title: result.data.title ? result.data.title : result.name,
              slug: `${result.query[0].value}`,
            });
          }
        }
      );
    }

    pages.push(sm);
  }

  return {
    props: {
      pages,
      ...(await serverSideTranslations(locale ? locale : 'en-us', ['common'])),
    },
  };
};
